import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { debounce, get } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill'; // Import ReactQuill type if needed for ref
import 'react-quill/dist/quill.snow.css';
import editorCSS from 'themes/styled/editorCSS';
import { removeCSSProperties } from 'utils/functions';
import DOMPurify from 'dompurify';

interface HTMLBlockProps {
  values: any;
  setFieldValue: any;
  path: string;
  timestampPath?: string;
  deps?: any[];
  saveOnDebounce?: boolean;
  fullHeight?: boolean;
  readOnly?: boolean;
}

const HTMLBlock = ({
  values,
  setFieldValue,
  path,
  deps = [],
  timestampPath,
  saveOnDebounce,
  fullHeight,
  readOnly = false
}: HTMLBlockProps) => {
  const theme = useTheme();
  const editorSx = editorCSS(theme, fullHeight);
  let htmlContent = get(values, path, '');
  if (typeof htmlContent !== 'string') {
    htmlContent = '';
  }

  // Store the content in a state
  const [content, setContent] = useState(htmlContent);
  const htmlRef = useRef('');
  const quillRef = useRef<ReactQuill>(null); // <-- Add ref for ReactQuill

  // Update the editor content when props change - improved to handle empty strings too
  useEffect(() => {
    // Always set content when deps change, regardless of whether htmlContent is empty or not
    setContent(htmlContent);
  }, deps); // eslint-disable-line

  const debouncedSetFieldValues = debounce(
    (path: string, cleanedHTML: string, timestampPath?: string) => {
      setFieldValue(path, cleanedHTML);
      if (timestampPath) setFieldValue(timestampPath, new Date());
    },
    250
  );

  const handleChange = (value: string) => {
    setContent(value);

    // Clean and sanitize the HTML
    const sanitizedHTML = DOMPurify.sanitize(value);
    const cleanedHTML = removeCSSProperties(sanitizedHTML, ['font-family']);
    htmlRef.current = cleanedHTML;

    if (saveOnDebounce) {
      debouncedSetFieldValues(path, cleanedHTML, timestampPath);
    }
  };

  const saveOnBlur = () => {
    // Only save if not debouncing, otherwise debounce handles it
    if (!saveOnDebounce) {
       setFieldValue(path, htmlRef.current);
       if (timestampPath) setFieldValue(timestampPath, new Date());
    }
  };

  // Add useEffect to handle toolbar focus
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const toolbar = quill.getModule('toolbar');
      const preventBlur = (e: MouseEvent) => {
        // Prevent the editor from losing focus when toolbar is clicked
        e.preventDefault();
      };
      // Ensure toolbar container exists before adding listener
      if (toolbar.container) {
        toolbar.container.addEventListener('mousedown', preventBlur);
      }


      // Cleanup listener on unmount
      return () => {
        // Check if container still exists before removing listener
        if (toolbar.container) {
            toolbar.container.removeEventListener('mousedown', preventBlur);
        }
      };
    }
  }, []); // Run only once on mount

  // Define Quill modules/toolbar options
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link'],
      [{ color: [] }]
    ]
  };

  // Define Quill formats to allow
  const formats = [
    'header',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent', // Note: 'indent' format requires indent/outdent buttons in toolbar or custom handling
    'link',
    'color',
    'align'
  ];

  return (
    <>
      <Box sx={{ ...editorSx, overflow: 'visible' }}>
        <ReactQuill
          readOnly={readOnly}
          ref={quillRef}
          theme="snow"
          value={content}
          onChange={handleChange}
          onBlur={saveOnBlur}
          modules={modules}
          formats={formats}
          preserveWhitespace={true}
        />
      </Box>
    </>
  );
};

export default HTMLBlock;
